/*
 * Copyright © 2022 EPO: European Patent Office.
 * ALL RIGHTS RESERVED
 */

import React from 'react';
import { makeStyles, Button, Box, Typography } from '@material-ui/core';
import DeleteIcon from '@material-ui/icons/Delete';
import { useTranslation } from 'react-i18next';
import Backdrop from '../../../../components/backdrop/Backdrop';
import useRemoveUserAccount from '../../../../redux/features/user/remove/useRemoveUserAccount';
import RemoveUserConfirmDialog from '../../../../components/user/RemoveUserConfirmDialog';
import RemoveUserErrorDialog from '../../../../components/user/RemoveUserErrorDialog';
import RemoveUserVerifyDialog from '../../../../components/user/RemoveUserVerifyDialog';
import RemoveUserSuccessDialog from '../../../../components/user/RemoveUserSuccessDialog';

const useStyles = makeStyles(theme => ({
  deleteAccountContainer: {
    display: 'flex',
    flexDirection: 'column',
    marginTop: '1.15rem',
  },
  subtitle: {
    fontSize: '1.2rem',
    fontWeight: '400',
    maxWidth: '35rem',
    marginTop: theme.spacing(2),
  },
}));

export default function UserDeleteAccount() {
  const classes = useStyles();
  const { t } = useTranslation();

  const {
    isLoading,
    validateRemoveUser,
    error,
    confirmationDialog,
    onToggleConfirmDialog,
    onSubmitConfirmDialog,
    verifyDialog,
    onToggleVerifyDialog,
    onSubmitVerifyDialog,
    onKeyUp,
    deleteButtonActive,
    successDialog,
    onToggleSuccessDialog,
    errorDialog,
    onToggleErrorDialog,
    onSubmitErrorDialog,
  } = useRemoveUserAccount();

  return (
    <Box>
      <Typography variant="h6" component="h6" gutterBottom>
        {t('user.management.delete-account')}
      </Typography>
      <Typography className={classes.subtitle} variant="h6" color="textSecondary" gutterBottom>
        {t('user.management.delete-account.description')}
      </Typography>
      <div className={classes.deleteAccountContainer}>
        {isLoading && <Backdrop blocking={true} />}
        <div>
          <Button
            aria-label={t('user.management.delete-account.button')}
            variant="contained"
            color="default"
            className={classes.button}
            startIcon={<DeleteIcon />}
            onClick={validateRemoveUser}
          >
            {t('user.management.delete-account.button')}
          </Button>
        </div>
        {confirmationDialog.open && (
          <RemoveUserConfirmDialog
            dialog={confirmationDialog}
            toggleDialog={open => onToggleConfirmDialog(open)}
            onSubmit={onSubmitConfirmDialog}
          />
        )}
        {errorDialog.open && (
          <RemoveUserErrorDialog
            errorDialog={errorDialog}
            onToggleErrorDialog={open => onToggleErrorDialog(open)}
            onSubmitErrorDialog={onSubmitErrorDialog}
            result={error}
          />
        )}
        {verifyDialog.open && (
          <RemoveUserVerifyDialog
            dialog={verifyDialog}
            deleteAvailable={deleteButtonActive}
            toggleDialog={onToggleVerifyDialog}
            onSubmit={onSubmitVerifyDialog}
            onKeyUp={onKeyUp}
          />
        )}
        {successDialog.open && (
          <RemoveUserSuccessDialog dialog={successDialog} toggleDialog={onToggleSuccessDialog} result={error} />
        )}
      </div>
    </Box>
  );
}
